import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';

import { IAvailableStore } from 'appointment-tool/types';
import { Logo } from 'common/components/Logo/Logo';
import { EView } from 'appointment-tool/constants/views';

import { LocationBar } from '../LocationBar';
import { StoreList } from '../StoreList';
import { ControlsContainer } from '../ControlsContainer';
import { ReflexAppointment } from '../ReflexAppointment';
import { breakpoints } from '../../constants';
import { useStateContext } from '../../context';
import { createMarkup, getAppointmentUrl } from '../../utils';

const LogoBar = styled(Box)`
    position: absolute;
    left: 0;
    bottom: 0;
    margin-top: auto;
    display: flex;
    justify-content: center;
    width: 100%;
    height: 75px;
    background-color: ${({ theme }) => theme.palette.primary.main};
    display: flex;
    align-items: center;

    ${Logo} {
        height: 100%;

        .MuiTypography-root {
            font-size: ${({ theme }) => theme.typography.body2.fontSize};
            color: ${({ theme }) => theme.palette.background.paper};
            line-height: 1;
        }

        svg {
            height: 50px;
        }

        svg path {
            fill: ${({ theme }) => theme.palette.background.paper};
        }
    }
`;

interface IProps {
    className?: string;
}

const BaseControls: React.FC<IProps> = ({ children, className }) => {
    const { availableStores, view, setSelectedStore, setView } =
        useStateContext();

    const {
        t: translate,
        i18n: { language },
    } = useTranslation();

    const handleClickStore = (store: IAvailableStore) => {
        const appointmentUrl = getAppointmentUrl(store.globalId);

        window.open(
            language === 'fr' ? appointmentUrl.fr : appointmentUrl.nl,
            '_blank'
        );
    };

    const handleClickBack = () => {
        setSelectedStore(undefined);
        setView(EView.StoreList);
    };

    const isStoreListView = view === EView.StoreList;

    return (
        <div className={className}>
            {children || (
                <>
                    <Box height="60px" mb={6}>
                        <Typography
                            color="primary"
                            dangerouslySetInnerHTML={createMarkup(
                                `${translate(
                                    'appointment.header'
                                )} ${'Pearle Opticiens'}`
                            )}
                            variant="h1"
                        />
                    </Box>
                    <ControlsContainer>
                        {isStoreListView && <LocationBar />}

                        {!isStoreListView && (
                            <ReflexAppointment onClickBack={handleClickBack} />
                        )}

                        {isStoreListView && availableStores && (
                            <StoreList
                                availableStores={availableStores}
                                onClickStore={handleClickStore}
                            />
                        )}
                    </ControlsContainer>
                </>
            )}
            <LogoBar>
                <Logo />
            </LogoBar>
        </div>
    );
};

export const Controls = styled(BaseControls)`
    position: relative;
    background-color: ${({ theme }) => theme.palette.background.paper};
    overflow: hidden;
    padding: ${({ theme }) =>
        `${theme.spacing(5)}px ${theme.spacing(5)}px ${theme.spacing(15)}px`};
    min-height: 100%;
    max-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;

    ${ControlsContainer} {
        overflow-y: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;

        ::-webkit-scrollbar {
            display: none;
        }

        max-height: 100%;
        height: 100%;
    }

    @media (${breakpoints.mediumMin}) {
        max-width: 475px;
        text-align: left;
        box-shadow: 6px 0px 13px -1px rgba(0, 0, 0, 0.12);
        z-index: 1;
    }

    h1 {
        font-size: 22px;
        text-align: center;
        font-weight: 300;
        margin-top: 0;

        strong {
            font-weight: 700;
        }

        @media (${breakpoints.mediumMin}) {
            font-size: 26px;
            text-align: left;
        }
    }
`;
